/**
 * The confirmation gate in front of every `runQueryText` execution.
 *
 * Extracted from `QueryEditorTab` so it can be tested by *running it* rather
 * than by re-implementing it in a test file or grepping the component's
 * source. `runQueryText` now does nothing but call this and switch on the
 * answer, so the decision below is the decision production makes.
 *
 * Two calibrations, deliberately different:
 *
 * - **Human path** — `checkRisk` (WS-E risk gate), unchanged. A missing WHERE clause is
 *   the signal, because the classic accident is a person forgetting one.
 * - **Agent path** (`ai-agent-request`, the execute-with-confirm protocol) —
 *   additionally `isAgentDestructiveQuery`, one pass over the **raw** query text
 *   looking for any token that can destroy data, hand out capability outside the
 *   database, or run SQL this app cannot read. Nothing is stripped first: a
 *   previous design removed comments and string literals before matching, and
 *   every disagreement between its opinion of where a comment ended and the
 *   engine's was a live bypass. (Plain `INSERT` is deliberately excluded: it is
 *   additive, and including it would leave a standing grant covering nothing but
 *   `SELECT`.) Under an "Always this session" grant no toast is shown, so this
 *   dialog is the only UI standing between an agent-authored statement and the
 *   database — and `DELETE FROM users WHERE 1=1`, the consent-bypass payload, is
 *   not something the missing-WHERE heuristic flags.
 *
 * The agent rule only ever *adds* confirmations; it cannot suppress one.
 */

import {
  isAgentDestructiveQuery,
  type DangerousQueryResult,
  type DatabaseEngineId,
} from '../../../../core/engines';
import type { QueryExecuteSource } from './useQueryExecution';
import { checkRisk } from '../../../sql-safety/riskGate';

export type ExecutionGateDecision =
  | { kind: 'nothing-to-run' }
  | { kind: 'confirm'; dangerInfo: DangerousQueryResult }
  | { kind: 'execute' };

export type ExecutionGateInput = {
  engineId: DatabaseEngineId;
  sql: string;
  source: QueryExecuteSource;
};

/**
 * Which sources carry SQL an autonomous agent wrote *and* run it without a
 * per-query confirmation toast. Only the execute-with-confirm protocol
 * qualifies: `mcp` (`ui_execute_query`) is a separate, long-standing surface
 * whose behaviour the ruling deliberately left alone.
 */
function isAgentAuthored(source: QueryExecuteSource): boolean {
  return source === 'ai-agent-request';
}

/**
 * The second half of the consent binding, evaluated when the user clicks
 * through the destructive-query dialog.
 *
 * `evaluateExecutionGate` decides whether to *ask*. Asking takes human-scale
 * time, and the tab underneath the dialog stays live the whole while: the
 * agent can rebind it to another connection or database, and the approved
 * statement would then land somewhere the user never saw. That is the same
 * class of bug `expectedSql` closed for the buffer, moved behind the dialog.
 *
 * The guard belongs to whoever owns the binding (`useAiExecutionRunner`, which
 * captured the approved target). `null` means "still the approved target";
 * anything else is a coded reason the caller hands straight back to the agent.
 * No guard at all is the human path, which has nothing to re-check.
 */
export type ConfirmedExecutionDecision =
  | { kind: 'execute'; sql: string }
  | { kind: 'refuse'; reason: string };

export function evaluateConfirmedExecution(input: {
  sql: string | null;
  guard?: () => string | null;
}): ConfirmedExecutionDecision {
  if (!input.sql) return { kind: 'refuse', reason: 'No query to execute.' };

  const reason = input.guard?.() ?? null;
  if (reason !== null) return { kind: 'refuse', reason };

  return { kind: 'execute', sql: input.sql };
}

export function evaluateExecutionGate(input: ExecutionGateInput): ExecutionGateDecision {
  if (!input.sql.trim()) return { kind: 'nothing-to-run' };

  // WS-E: the human path routes through the risk gate, which evaluates the
  // pattern tables per grammar statement slice over a code-only projection
  // (so a DROP inside a comment or a string literal is not a verdict).
  const dangerous = checkRisk(input.sql, input.engineId);
  if (dangerous.dangerous) return { kind: 'confirm', dangerInfo: dangerous };

  if (isAgentAuthored(input.source)) {
    const agentDangerous = isAgentDestructiveQuery(input.engineId, input.sql);
    if (agentDangerous.dangerous) return { kind: 'confirm', dangerInfo: agentDangerous };
  }

  return { kind: 'execute' };
}
